import { z } from 'zod';

export const CAMPS = ['ORANGE', 'TURQUOISE'] as const;
export type Camp = (typeof CAMPS)[number];

const positive = () => z.number().finite().positive();
const nonNegative = () => z.number().finite().nonnegative();
const unitInterval = () => z.number().finite().min(0).max(1);

/**
 * Values marked `z.literal` are design locks.
 * They can only change through a recorded human game-design decision.
 */
export const BalanceSchema = z.strictObject({
  id: z.string().min(1),
  version: z.number().int().positive(),
  player: z.strictObject({
    radius: positive(),
    mass: positive(),
    acceleration: positive(),
    maxInputSpeed: positive(),
    damping: nonNegative(),
    hardSpeedCap: positive(),
    wallRestitution: unitInterval(),
    playerRestitution: unitInterval(),
    tangentialFriction: unitInterval(),
    stunSeconds: z.literal(3),
    protectionSeconds: nonNegative(),
  }),
  golden: z.strictObject({
    count: z.number().int().min(1).max(12),
    radius: positive(),
    mass: positive(),
    cruiseSpeed: positive(),
    maxSpeed: positive(),
    restitution: unitInterval(),
    playerContactRestitution: unitInterval(),
    overspeedDecayPerSecond: nonNegative(),
    underspeedRecoveryPerSecond: nonNegative(),
    reboundJitterDegrees: z.number().finite().min(0).max(5),
    minAxisAngleDegrees: z.number().finite().min(0).max(44),
  }),
  fuse: z.strictObject({
    fuseSeconds: z.literal(3),
    clearPreviewSeconds: positive(),
    chainDelaySeconds: positive(),
  }),
  explosion: z.strictObject({
    range: z.number().int().min(1).max(20),
    beamWidth: z.number().finite().positive().max(1),
    lethalSeconds: positive(),
    visualSeconds: positive(),
    neutralEjectionSpeed: positive(),
  }),
  respawn: z.strictObject({
    absentSeconds: nonNegative(),
    materialiseSeconds: positive(),
    minPlayerDistance: nonNegative(),
  }),
  bonus: z.strictObject({
    intervalSeconds: positive(),
    lifetimeSeconds: positive(),
    radius: positive(),
  }),
  totem: z.strictObject({
    hp: z.literal(3),
    /** Ceiling for lives won back with 1Up bonuses. */
    maxHp: z.number().int().min(3).max(9),
  }),
  match: z.strictObject({
    regulationSeconds: positive(),
    countdownSeconds: nonNegative(),
    rematchCountdownSeconds: nonNegative(),
    suddenDeathAutoArmAfterSeconds: nonNegative(),
    suddenDeathAutoArmIntervalSeconds: positive(),
    suddenDeathUnlimitedRangeAfterSeconds: nonNegative(),
  }),
});
export type Balance = z.infer<typeof BalanceSchema>;

const Vec2Schema = z.strictObject({ x: z.number().finite(), y: z.number().finite() });
const CellTupleSchema = z.tuple([z.number().int(), z.number().int()]);

/** Raw map file. Cells are 1-based `[column, row]`. */
export const MapFileSchema = z.strictObject({
  id: z.string().min(1),
  version: z.number().int().positive(),
  name: z.string().min(1),
  grid: z.strictObject({
    columns: z.number().int().min(3).max(32),
    rows: z.number().int().min(3).max(32),
  }),
  layout: z.array(z.string()),
  blockCollisionSize: z.number().finite().positive().max(1),
  players: z.strictObject({ ORANGE: Vec2Schema, TURQUOISE: Vec2Schema }),
  goldenSpawns: z.array(CellTupleSchema).min(1),
  forbiddenSpawnRows: z.array(z.number().int()),
});
export type MapFile = z.infer<typeof MapFileSchema>;

const ScenarioVec = z.strictObject({ x: z.number().finite(), y: z.number().finite() });

const ScenarioPlayer = z.strictObject({
  x: z.number().finite(),
  y: z.number().finite(),
  vx: z.number().finite().optional(),
  vy: z.number().finite().optional(),
  status: z.enum(['NORMAL', 'STUNNED', 'PROTECTED']).optional(),
  statusRemainingTicks: z.number().int().positive().optional(),
});

const ScenarioGolden = z.strictObject({
  x: z.number().finite(),
  y: z.number().finite(),
  vx: z.number().finite().optional(),
  vy: z.number().finite().optional(),
  status: z.enum(['NEUTRAL', 'ARMED']).optional(),
  fuseRemainingTicks: z.number().int().positive().optional(),
  toucher: z.enum(CAMPS).optional(),
});

const ScenarioInput = z.strictObject({
  from: z.number().int().nonnegative(),
  to: z.number().int().nonnegative(),
  ORANGE: ScenarioVec.optional(),
  TURQUOISE: ScenarioVec.optional(),
});

/** A reproducible scene. Ticks in `inputs` are relative to the start. */
export const ScenarioSchema = z.strictObject({
  id: z.string().regex(/^[a-z0-9-]+$/),
  number: z.number().int().positive(),
  title: z.string().min(1),
  description: z.string().min(1),
  map: z.enum(['arena', 'fortress']),
  seed: z.number().int().nonnegative(),
  durationTicks: z.number().int().positive(),
  setup: z.strictObject({
    phase: z.enum(['COUNTDOWN', 'REGULATION', 'OVERTIME', 'SUDDEN_DEATH']).optional(),
    regulationRemainingTicks: z.number().int().nonnegative().optional(),
    suddenDeathElapsedTicks: z.number().int().nonnegative().optional(),
    totemHp: z
      .strictObject({
        ORANGE: z.number().int().nonnegative(),
        TURQUOISE: z.number().int().nonnegative(),
      })
      .optional(),
    players: z
      .strictObject({ ORANGE: ScenarioPlayer.optional(), TURQUOISE: ScenarioPlayer.optional() })
      .optional(),
    goldens: z.array(ScenarioGolden).optional(),
  }),
  inputs: z.array(ScenarioInput).default([]),
});
export type Scenario = z.infer<typeof ScenarioSchema>;
export type ScenarioInputSpan = z.infer<typeof ScenarioInput>;
